import * as React from "react";
import { Link } from "react-router-dom";
import { bindActionCreators } from "redux";

import { push as reduxPush } from "react-router-redux";
import { connect } from "react-redux";
import { Role, HLHLContactRole } from "@eave/shared/types/api";
import analytics from "web_modules/utils/segment";

import APIClient from "./APIClient";
import {
  operatorHLHLContactRoleFromToken,
  operatorIdFromToken,
  rolesFromToken,
  saveSessionToken,
  tokenHasRoles,
} from "./shared/session";
import saveHLHLContactRole from "./shared/LoggedInContainer/actions";

import { Logo } from "@eave/shared/components/Logo";
require("../assets/stylesheets/pages/user-credentials.scss");

declare let window: any;

const trusURIRegex =
  /((https:\/\/)((integration|staging|hotfix|homeloans|www)\.)?trus\.(io|local))|(http:\/\/localhost:8080)|(https:\/\/trus.homelight.com)/g;
const trusBranchURIRegex = /((https:\/\/)[a-z]+\.trus\.io)|(https:\/\/trus.homelight.com)/g;
const integrationOrigin = "https://trus.homelight.com";

class Login extends React.Component<any, any> {
  static operatorLogin(): boolean {
    return window.location.pathname === "/operators/login";
  }

  static testOperatorLogin(): boolean {
    return window.location.pathname === "/testoperators/login";
  }

  static redirectPath(destination_key, destination_path, search, token) {
    const query = new URLSearchParams(search);
    let route = "";

    // check for default for overrides to default routes
    if (destination_key === "borrower_default" || destination_key === "operator_default") {
      if (query.get("redirect")) {
        route = decodeURIComponent(query.get("redirect"));
      } else if (destination_path && destination_path !== "") {
        route = decodeURIComponent(destination_path);
      }
    }

    // use defaults if no overrides are found
    if (route === "") {
      const routeMap = {
        [Role.Admin]: "/underwriter",

        // NOTE: jjw: LOs used to default to "/loan-officer"; trying this out per richie's request
        // (see: https://homelight.slack.com/archives/CQ0RK8B2N/p1602543394052600)
        [Role.LoanOfficer]: "/underwriter",

        [Role.Underwriter]: "/underwriter",
        [Role.CapitalMarkets]: "/capital-markets",
        [Role.SettlementAgent]: "/settlement-agent",
        [Role.CashClose]: "/reporting/pipeline",
        [Role.CashCloseAdmin]: "/reporting/pipeline",
      };

      // If a user has multiple roles order the routing from least to greatest
      // information. Eg. LO dashboard has less information than underwriter
      // dashboard.
      if (tokenHasRoles(token, Role.LoanOfficer)) {
        route = routeMap[Role.LoanOfficer];
      } else if (tokenHasRoles(token, Role.Underwriter)) {
        route = routeMap[Role.Underwriter];
      } else if (tokenHasRoles(token, Role.Admin)) {
        route = routeMap[Role.Admin];
      } else if (tokenHasRoles(token, Role.CapitalMarkets)) {
        route = routeMap[Role.CapitalMarkets];
      } else if (tokenHasRoles(token, Role.SettlementAgent)) {
        route = routeMap[Role.SettlementAgent];
      } else if (tokenHasRoles(token, Role.CashClose)) {
        route = routeMap[Role.CashClose];
      } else if (tokenHasRoles(token, Role.CashCloseAdmin)) {
        route = routeMap[Role.CashCloseAdmin];
      } else {
        route = "/404";
      }
    }

    return route;
  }

  parent = null;

  parentOrigin = "";

  messageListener = null;

  componentDidMount() {
    if (!window.location.origin.match(trusURIRegex)) {
      this.messageListener = this.setupParentEventListener();
    }

    // eslint-disable-next-line no-restricted-globals
    if (window !== top) {
      this.messageListener = this.setupIFrameEventListener();
    }

    if (Login.testOperatorLogin()) {
      const email = new URLSearchParams(window.location.search).get("email");
      const payload = { email: "" };
      if (email !== "") {
        payload.email = email;
      }

      // do all the magic here, no button clicking or anything
      APIClient.post("/operator_test_auth", payload).then((responseObject: any) => {
        if (responseObject.signed_token) {
          const {
            push,
            location: { search },
          } = this.props;
          saveSessionToken(responseObject.signed_token);
          push(
            Login.redirectPath(
              responseObject.destination,
              responseObject.path,
              search,
              responseObject.signed_token,
            ),
          );
        } else {
          alert("Unauthorized");
        }
      });
      return;
    }

    window.gapi.signin2.render("g-signin2", {
      theme: "dark",
      onsuccess: this.onGoogleSuccess.bind(this),
    });
  }

  componentWillUnmount() {
    if (this.messageListener) {
      window.removeEventListener("event", this.messageListener);
    }
  }

  onGoogleSuccess(googleUser) {
    APIClient.post("/google_auth", {
      id_token: googleUser.getAuthResponse().id_token,
    }).then((responseObject: any) => {
      if (responseObject.signed_token) {
        const {
          push,
          location: { search },
          persistHLHLContactRole,
        } = this.props;

        const path = Login.redirectPath(
          responseObject.destination,
          responseObject.path,
          search,
          responseObject.signed_token,
        );
        if (this.parent !== null && this.parentOrigin !== "") {
          this.parent.postMessage(
            JSON.stringify({ eaveToken: responseObject.signed_token, redirect: path }),
            this.parentOrigin,
          );
        } else {
          const { signed_token } = responseObject;
          saveSessionToken(signed_token);
          persistHLHLContactRole(operatorHLHLContactRoleFromToken(signed_token));

          const id = operatorIdFromToken(signed_token);
          const roles = rolesFromToken(signed_token);
          analytics.identify(`operator_${id}`, {
            user_type: "operator",
            user_id: id,
            user_roles: roles,
          });
          push(path);
        }
      } else {
        alert("Unauthorized");
      }
    });
  }

  setupParentEventListener() {
    return window.addEventListener(
      "message",
      (e: MessageEvent) => {
        if (e.origin === integrationOrigin) {
          const { eaveToken, redirect } = JSON.parse(e.data);
          if (eaveToken && redirect) {
            const { push } = this.props;
            saveSessionToken(eaveToken);
            push(redirect);
          }
        }
      },
      false,
    );
  }

  setupIFrameEventListener() {
    return window.addEventListener(
      "message",
      (e: MessageEvent) => {
        if (e.origin.match(trusBranchURIRegex) && e.origin !== integrationOrigin) {
          this.parent = e.source;
          this.parentOrigin = e.data;
        }
      },
      false,
    );
  }

  render() {
    if (!window.location.href.match(trusURIRegex)) {
      const url = `${integrationOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`;

      return (
        <iframe
          id="loginFrame"
          title="loginFrame"
          src={url}
          allow="fullscreen"
          frameBorder="0"
          height="100%"
          width="100%"
          onLoad={() => {
            const frame = window.frames.loginFrame as HTMLIFrameElement;
            frame.contentWindow.postMessage(window.location.origin, integrationOrigin);
          }}
        />
      );
    }

    return (
      <div className="user-credentials">
        <Link to="/" className="logo">
          <Logo textColor="light" />
        </Link>
        <h1>Operator Dashboard</h1>
        <div className="user-credentials__form">
          <h3>Login</h3>
          {Login.operatorLogin() && <div id="g-signin2" />}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  push: bindActionCreators(reduxPush, dispatch),
  persistHLHLContactRole: (role: HLHLContactRole) => dispatch(saveHLHLContactRole(role)),
});

export default connect(null, mapDispatchToProps)(Login);
